<p-toast [life]="10000"></p-toast>

<div class="product-form-page p-fluid">
    <p-progressBar mode="indeterminate" class="page-progress" *ngIf="isLoading"></p-progressBar>

    <app-page-detail-hero eyebrow="Product" [title]="editMode ? 'Edit Product' : 'New Product'"
        subtitle="Define the catalog header once, then maintain the priced sellable variants used on transactions."
        icon="pi pi-box" [auditStamp]="editMode && product?.productID ? product : null">
        <div actions>
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (click)="cancel()"></p-button>
            <p-button label="Save Product" icon="pi pi-check" [loading]="isLoading" (click)="saveProduct()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    @if (product?.systemStandard) {
    <section class="system-standard-notice">
        <i class="pi pi-lock"></i>
        <div>
            <strong>System-standard shipment product</strong>
            <span>This product cannot be deleted or converted to another product type. Commercial rates, tax, currency, and GL accounts remain configurable.</span>
        </div>
    </section>
    }

    <section class="form-shell">
        <form [formGroup]="productForm" class="product-form-grid">
            <app-page-form-section icon="pi pi-box" title="Product Overview" subtitle="Core identity, classification, and description used across order entry.">
                <div class="section-grid">
                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="name" type="text" pInputText formControlName="name">
                                <label for="name">Product name *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="hasError('name')">Product name is required.</small>
                    </div>

                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="productCode" type="text" pInputText formControlName="productCode">
                                <label for="productCode">Product code *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="hasError('productCode')">Product code is required.</small>
                    </div>

                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select
                                    inputId="productType"
                                    [options]="productTypes"
                                    formControlName="productType"
                                    optionLabel="label"
                                    optionValue="value"
                                    styleClass="w-full">
                                </p-select>
                                <label for="productType">Product type *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="hasError('productType')">Product type is required.</small>
                    </div>

                    <div class="field span-2">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <textarea id="description" rows="3" pInputTextarea formControlName="description"></textarea>
                                <label for="description">Description</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field span-2">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-language"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <textarea id="descriptionLocal" rows="3" dir="rtl" pInputTextarea formControlName="descriptionLocal"></textarea>
                                <label for="descriptionLocal">Arabic invoice description</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small>Copied as a default when a receivable or invoice line is created. It remains editable on that line.</small>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-warehouse" title="Inventory Managed" subtitle="Link sellable variants to warehouse stock, UOM packs, FEFO lots, and frozen temperature controls.">
                <div class="inventory-layout">
                    <label class="inventory-toggle">
                        <input type="checkbox" formControlName="inventoryManaged">
                        <span><strong>Manage this product in warehouse inventory</strong><small>Accepted PO stock enters quality hold; released lots become available to sales orders.</small></span>
                    </label>
                    @if (productForm.get('inventoryManaged')?.value) {
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-arrows-h"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="baseUomId" formControlName="baseUomId" [options]="inventoryUoms" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" styleClass="w-full"></p-select><label for="baseUomId">Base unit *</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-shopping-cart"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="defaultPurchaseUomId" formControlName="defaultPurchaseUomId" [options]="inventoryUoms" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" styleClass="w-full"></p-select><label for="defaultPurchaseUomId">Purchase pack unit</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-sync"></i></p-inputGroupAddon><p-iftaLabel><p-inputNumber inputId="purchaseFactor" formControlName="purchaseFactor" [minFractionDigits]="1" [maxFractionDigits]="6"></p-inputNumber><label for="purchaseFactor">Purchase pack to base</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-send"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="defaultSalesUomId" formControlName="defaultSalesUomId" [options]="inventoryUoms" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" styleClass="w-full"></p-select><label for="defaultSalesUomId">Sales pack unit</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon><p-iftaLabel><p-inputNumber inputId="salesFactor" formControlName="salesFactor" [minFractionDigits]="1" [maxFractionDigits]="6"></p-inputNumber><label for="salesFactor">Sales pack to base</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="defaultWarehouseId" formControlName="defaultWarehouseId" [options]="warehouses" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" styleClass="w-full"></p-select><label for="defaultWarehouseId">Default warehouse *</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="defaultConditionId" formControlName="defaultConditionId" [options]="itemConditions" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" styleClass="w-full"></p-select><label for="defaultConditionId">Default stock condition</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-barcode"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="trackingMode" formControlName="trackingMode" [options]="inventoryTrackingModes" optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select><label for="trackingMode">Tracking mode</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon><p-iftaLabel><p-inputNumber inputId="shelfLifeDays" formControlName="shelfLifeDays" [useGrouping]="false"></p-inputNumber><label for="shelfLifeDays">Shelf life (days)</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-down"></i></p-inputGroupAddon><p-iftaLabel><p-inputNumber inputId="minimumTemperatureC" formControlName="minimumTemperatureC" suffix=" °C"></p-inputNumber><label for="minimumTemperatureC">Minimum temperature</label></p-iftaLabel></p-inputGroup></div>
                    <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-up"></i></p-inputGroupAddon><p-iftaLabel><p-inputNumber inputId="maximumTemperatureC" formControlName="maximumTemperatureC" suffix=" °C"></p-inputNumber><label for="maximumTemperatureC">Maximum temperature</label></p-iftaLabel></p-inputGroup></div>
                    }
                </div>
            </app-page-form-section>

            @if (productForm.get('productType')?.value === 'SERVICE') {
            <app-page-form-section icon="pi pi-sitemap" title="Logistics Usage" subtitle="Enable this service in shipment, clearance, container-yard, or warehouse billing workflows. Rates, tax, currency, and GL accounts become operational defaults.">
                <div actions>
                    <p-button label="Add Usage" icon="pi pi-plus" severity="secondary" [outlined]="true"
                        (click)="addLogisticsUsage()"></p-button>
                </div>
                <div class="variant-stack" formArrayName="logisticsUsages">
                    @for (usageGroup of logisticsUsages.controls; track $index; let i = $index) {
                    <section class="variant-card" [formGroupName]="i">
                        <div class="variant-card-header">
                            <div>
                                <strong>{{ usageGroup.get('systemStandard')?.value ? 'Standard mapping' : 'Logistics mapping ' + (i + 1) }}</strong>
                                <span>Operational classification remains available for rating and reporting.</span>
                            </div>
                            @if (!usageGroup.get('systemStandard')?.value) {
                            <button pButton type="button" icon="pi pi-trash" class="p-button-text p-button-danger"
                                (click)="removeLogisticsUsage(i)"></button>
                            }
                        </div>
                        <div class="variant-grid">
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-th-large"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'usageModule_' + i" formControlName="usageModule"
                                        [options]="logisticsUsageModules" optionLabel="label" optionValue="value"
                                        styleClass="w-full"></p-select>
                                    <label [for]="'usageModule_' + i">Module *</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'operationalCode_' + i" formControlName="operationalCode"
                                        [options]="operationalCodeOptions(usageGroup.get('usageModule')?.value)"
                                        optionLabel="label" optionValue="value" [filter]="true" styleClass="w-full"></p-select>
                                    <label [for]="'operationalCode_' + i">Operational code *</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-sparkles"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'defaultVariant_' + i" formControlName="defaultVariantId"
                                        [options]="productVariants.getRawValue()" optionLabel="name" optionValue="variantID"
                                        [showClear]="true" styleClass="w-full"></p-select>
                                    <label [for]="'defaultVariant_' + i">Default variant</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <label class="inventory-toggle">
                                <input type="checkbox" formControlName="active">
                                <span><strong>Active mapping</strong><small>Show this product in the selected module catalog.</small></span>
                            </label>
                        </div>
                    </section>
                    }
                    @if (!logisticsUsages.length) {
                    <div class="empty-state-inline">No logistics usage configured. The product remains available only in order entry.</div>
                    }
                </div>
            </app-page-form-section>
            }

            <app-page-form-section icon="pi pi-wallet" title="Shared Defaults" subtitle="Shared tax and currency defaults inherited by product transactions.">
                <div class="section-grid">
                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber
                                    inputId="taxRate"
                                    formControlName="taxRate"
                                    mode="decimal"
                                    [minFractionDigits]="2"
                                    [maxFractionDigits]="2"
                                    suffix="%"
                                    styleClass="w-full">
                                </p-inputNumber>
                                <label for="taxRate">Tax rate</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>

                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="currencyCode" type="text" pInputText formControlName="currencyCode" maxlength="3">
                                <label for="currencyCode">Currency *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="hasError('currencyCode')">Currency is required.</small>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-calculator" title="Accounting Defaults" subtitle="Optional product-level overrides. Sales credit income; purchases debit expense or COGS.">
                <div class="section-grid">
                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-arrow-down-right"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="incomeAccountId" [options]="glAccounts" optionLabel="label" optionValue="value"
                                    formControlName="incomeAccountId" [filter]="true" filterBy="label" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="incomeAccountId">Income account (sales credit)</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-arrow-up-right"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="expenseAccountId" [options]="glAccounts" optionLabel="label" optionValue="value"
                                    formControlName="expenseAccountId" [filter]="true" filterBy="label" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="expenseAccountId">Expense / COGS account (purchase debit)</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-link" title="CRM Client Item Codes" subtitle="Map each customer's or supplier's printed item code to this catalog product for AI and order entry.">
                <div actions>
                    <p-button label="Add Mapping" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="addCrmClientItemCode()"></p-button>
                </div>
                <div class="variant-stack" formArrayName="crmClientItemCodes">
                    @for (mappingGroup of crmClientItemCodes.controls; track $index; let i = $index) {
                    <section class="variant-card" [formGroupName]="i">
                        <div class="variant-card-header">
                            <div><strong>Client item code {{ i + 1 }}</strong><span>Matches the printed customer or supplier code to this product.</span></div>
                            <button pButton pRipple type="button" icon="pi pi-trash" class="p-button-text p-button-danger" (click)="removeCrmClientItemCode(i)"></button>
                        </div>
                        <div class="variant-grid">
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'crmClient_' + i" [options]="crmClients" optionLabel="label" optionValue="value" formControlName="crmClientId" [filter]="true" filterBy="label" styleClass="w-full"></p-select>
                                    <label [for]="'crmClient_' + i">CRM client *</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'clientItemCode_' + i" pInputText formControlName="clientItemCode"><label [for]="'clientItemCode_' + i">Client item code *</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'clientItemDescription_' + i" pInputText formControlName="clientItemDescription"><label [for]="'clientItemDescription_' + i">Client description</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <label class="inventory-toggle"><input type="checkbox" formControlName="active"><span><strong>Active mapping</strong><small>Use this code when matching uploaded documents.</small></span></label>
                        </div>
                    </section>
                    }
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-sparkles" title="Sellable Variants" subtitle="Each variant is a priced transaction row used directly in sales and purchase documents.">
                <div actions>
                    <p-button label="Add Variant" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="addProductVariant()"></p-button>
                </div>

                <div class="variant-stack" formArrayName="productVariants">
                    @for (variantGroup of productVariants.controls; track $index; let i = $index) {
                    <section class="variant-card" [formGroupName]="i">
                        <div class="variant-card-header">
                            <div>
                                <strong>Variant {{ i + 1 }}</strong>
                                <span>Define the code and effective commercials used on transactions.</span>
                            </div>
                            @if (!variantGroup.get('systemStandard')?.value) {
                            <button
                                pButton
                                pRipple
                                type="button"
                                icon="pi pi-trash"
                                class="p-button-text p-button-danger"
                                (click)="removeProductVariant(i)">
                            </button>
                            }
                        </div>

                        <div class="variant-grid">
                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input [id]="'variantName_' + i" type="text" pInputText formControlName="name">
                                        <label [for]="'variantName_' + i">Variant name *</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                <small class="p-error" *ngIf="hasVariantError(i, 'name')">Variant name is required.</small>
                            </div>

                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input [id]="'variantCode_' + i" type="text" pInputText formControlName="variantCode">
                                        <label [for]="'variantCode_' + i">Variant code *</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                <small class="p-error" *ngIf="hasVariantError(i, 'variantCode')">Variant code is required.</small>
                            </div>

                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-sort-alt"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-inputNumber
                                            [inputId]="'rateAdjustment_' + i"
                                            formControlName="rateAdjustment"
                                            mode="decimal"
                                            [minFractionDigits]="2"
                                            [maxFractionDigits]="2"
                                            styleClass="w-full">
                                        </p-inputNumber>
                                        <label [for]="'rateAdjustment_' + i">Standard rate</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>

                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-down"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-inputNumber
                                            [inputId]="'buyingRateAdjustment_' + i"
                                            formControlName="buyingRateAdjustment"
                                            mode="decimal"
                                            [minFractionDigits]="2"
                                            [maxFractionDigits]="2"
                                            styleClass="w-full">
                                        </p-inputNumber>
                                        <label [for]="'buyingRateAdjustment_' + i">Buying rate</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>

                            <div class="field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-up"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-inputNumber
                                            [inputId]="'sellingRateAdjustment_' + i"
                                            formControlName="sellingRateAdjustment"
                                            mode="decimal"
                                            [minFractionDigits]="2"
                                            [maxFractionDigits]="2"
                                            styleClass="w-full">
                                        </p-inputNumber>
                                        <label [for]="'sellingRateAdjustment_' + i">Selling rate</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                        </div>
                    </section>
                    }
                </div>
            </app-page-form-section>

        </form>
    </section>
</div>
